import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Briefcase, ShieldCheck, Check, Anchor, ArrowRight } from "lucide-react";
import { GoogleButton } from "../GoogleButton";

const FEATURES = [
  { icon: Users, title: "Network & Connect", text: "Instantly chat with fellow sailors, hiring managers, and maritime institutes." },
  { icon: Briefcase, title: "Genuine Jobs", text: "Access hand-picked sea contracts and courses tailored to your rank and experience." },
  { icon: ShieldCheck, title: "Verified Crew Profile", text: "Build a trusted online resume with complete CDC, sea service history, and certificates." },
];

const ROLES = [
  { id: "seafarer", label: "Seafarer" },
  { id: "company", label: "Company" },
  { id: "institute", label: "Institute" },
];

const Badge = () => (
  <div className="inline-flex items-center gap-2.5 rounded-full border border-orange-200 bg-orange-50/90 px-3.5 py-1.5 text-xs font-semibold text-[#c2410c]" data-testid="hero-badge">
    <span className="h-2 w-2 rounded-full bg-[#FF4500]" /> India's Verified Merchant Navy Network
  </div>
);

const FeatureTile = ({ icon: Icon, title, text }) => (
  <div className="group rounded-2xl border border-stone-200/90 bg-white/85 p-5 backdrop-blur-sm transition-colors duration-200 hover:border-orange-300 hover:bg-white">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-[#FF4500]"><Icon className="h-5 w-5" /></div>
    <h3 className="font-heading mt-4 text-base font-bold text-slate-950">{title}</h3>
    <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{text}</p>
  </div>
);

const Tick = ({ children }) => (
  <div className="flex items-center gap-2">
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-3 w-3" strokeWidth={3} /></span>
    <span>{children}</span>
  </div>
);

const GetStartedCard = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState("seafarer");
  return (
    <div id="get-started" className="indos-card-shadow relative flex flex-col rounded-3xl bg-white p-6 sm:p-9 lg:h-full lg:justify-between" data-testid="get-started-card">
      <div className="flex items-center gap-3 border-b border-stone-100 pb-5">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FF4500] text-white shadow-md shadow-orange-600/25"><Anchor className="h-6 w-6" /></span>
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-[#FF4500]">Quick Boarding</span>
          <h2 className="font-heading text-xl font-bold text-slate-950">Get Started with Indosbook</h2>
        </div>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-slate-600 lg:mt-0">Create your seafarer, company or institute account and start building your presence at sea.</p>

      <div className="mt-5 lg:mt-0">
        <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Select your role</span>
        <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-stone-100 p-1" role="tablist" aria-label="Account role">
          {ROLES.map((r) => (
            <button key={r.id} type="button" role="tab" aria-selected={role === r.id} onClick={() => setRole(r.id)} data-testid={`hero-role-${r.id}`}
              className={`rounded-lg py-2 text-xs font-semibold transition-colors ${role === r.id ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-950"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-3 lg:mt-0">
        <button type="button" onClick={() => navigate(`/signup?role=${role}`)} data-testid="hero-create-account"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#FF4500] text-sm font-semibold text-white shadow-md shadow-orange-600/25 transition-colors hover:bg-[#e03d00]">
          Create Account <ArrowRight className="h-4 w-4" />
        </button>
        <GoogleButton label="Continue with Google" />
      </div>

      <div className="my-6 flex items-center gap-3 lg:my-0">
        <span className="h-px flex-1 bg-stone-200" /><span className="text-xs font-medium text-slate-400">or</span><span className="h-px flex-1 bg-stone-200" />
      </div>
      <p className="text-center text-xs text-slate-600">
        Already have an account?{" "}
        <button onClick={() => navigate("/login")} data-testid="hero-login-link"
          className="font-semibold text-slate-950 underline decoration-[#FF4500] decoration-2 underline-offset-4 hover:text-[#FF4500]">Log In</button>
      </p>
    </div>
  );
};

export const Hero = () => (
  <section id="top-hero" className="indos-hero-mesh indos-grid-pattern relative overflow-hidden pb-20 pt-10 sm:pt-16 lg:pb-28 lg:pt-20" data-testid="landing-hero">
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-10">
        <div className="indos-reveal lg:col-span-7 lg:pr-6">
          <Badge />
          <h1 className="font-heading mt-6 text-4xl font-extrabold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl lg:leading-[1.06]">
            <span className="text-[#0B2D8C]" data-testid="hero-title-blue">One Nation,</span>
            <span className="block text-[#FF4500]">One Maritime Community.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="hero-subtitle">
            Join <strong className="font-semibold text-slate-900">9,000+ Indian seafarers</strong>, <strong className="font-semibold text-slate-900">400+ shipping companies</strong> &amp; <strong className="font-semibold text-slate-900">110+ DG Shipping-approved institutes</strong> in one dedicated hub.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {FEATURES.map((f) => <FeatureTile key={f.title} {...f} />)}
          </div>
          <div className="mt-8 flex flex-wrap lg:h-5 items-center gap-4 text-xs font-medium text-slate-500" data-testid="hero-ticks">
            <Tick>100% Free for Seafarers</Tick>
            <span className="hidden text-stone-300 sm:inline">|</span>
            <Tick>Direct RPSL &amp; Fleet Recruiters</Tick>
            <span className="hidden text-stone-300 sm:inline">|</span>
            <Tick>DG-Approved Institutes</Tick>
          </div>
        </div>
        <div className="indos-reveal lg:col-span-5 lg:self-stretch lg:pb-[52px]" style={{ animationDelay: "120ms" }}>
          <GetStartedCard />
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
